import { Component } from '@theme/component';
import { StandardEvents } from '@shopify/events';

/**
 * Keeps the product page "Inquire on WhatsApp" link in sync with the selected
 * variant. The link is fully rendered by Liquid for the initial variant
 * (snippets/sm-whatsapp-url.liquid); this only rebuilds it after a variant change,
 * using the same message lines as the Liquid builder.
 *
 * @typedef {object} Refs
 * @property {HTMLAnchorElement} link - The wa.me link.
 *
 * @extends {Component<Refs>}
 */
class SmWhatsappInquiry extends Component {
  requiredRefs = ['link'];

  /** @type {AbortController | undefined} */
  #listeners;

  connectedCallback() {
    super.connectedCallback();

    this.#listeners?.abort();
    this.#listeners = new AbortController();

    // ProductSelectEvent bubbles from the variant picker inside the same section.
    const scope = this.closest('.shopify-section') ?? document;
    scope.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, {
      signal: this.#listeners.signal,
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#listeners?.abort();
  }

  /**
   * @param {Event & { promise?: Promise<{ detail?: { productId?: string, resource?: any, html?: Document } }> }} event
   */
  #handleProductSelect = async (event) => {
    if (!event.promise) return;

    try {
      const { detail } = await event.promise;
      const variant = detail?.resource;
      if (!variant?.id) return;
      if (detail.productId && String(detail.productId) !== this.dataset.productId) return;

      // The re-rendered section already holds the correctly formatted price.
      const price = detail.html?.querySelector('product-price .price')?.textContent?.trim() ?? '';
      this.refs.link.href = this.#buildUrl(variant, price);
    } catch (error) {
      if (error instanceof Error && error.name === 'AbortError') return;
      console.warn('[sm-whatsapp-inquiry] Could not update the WhatsApp link:', error);
    }
  };

  /**
   * @param {{ id: number | string, title?: string, price?: number }} variant
   * @param {string} price - Formatted price of the variant.
   * @returns {string}
   */
  #buildUrl(variant, price) {
    const { number, intro, productTitle, productUrl, hasVariants, labelProduct, labelVariant, labelPrice, labelLink } =
      this.dataset;

    const lines = [intro ?? '', '', `${labelProduct}: ${productTitle}`];

    if (hasVariants === 'true' && variant.title) lines.push(`${labelVariant}: ${variant.title}`);
    if (price && Number(variant.price) > 0) lines.push(`${labelPrice}: ${price}`);

    const link = new URL(productUrl ?? window.location.href, window.location.origin);
    link.searchParams.set('variant', String(variant.id));
    lines.push(`${labelLink}: ${link.toString()}`);

    return `https://wa.me/${number}?text=${encodeURIComponent(lines.join('\n'))}`;
  }
}

if (!customElements.get('sm-whatsapp-inquiry')) {
  customElements.define('sm-whatsapp-inquiry', SmWhatsappInquiry);
}
